import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { track } from "@/lib/analytics";

const SESSION_KEY = "dcc_cart_session";
const Ctx = createContext(null);

function getOrCreateSessionId() {
  try {
    let s = localStorage.getItem(SESSION_KEY);
    if (!s) {
      s = `anon-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(SESSION_KEY, s);
    }
    return s;
  } catch { return null; }
}

function headers() {
  const s = getOrCreateSessionId();
  return s ? { "X-Session-Id": s } : {};
}

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(false);
  const [merged, setMerged] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cart", { headers: headers() });
      setCart(data);
    } catch (e) { /* silent */ } finally { setLoading(false); }
  }, []);

  // Auto-merge on login
  useEffect(() => {
    if (!user || merged) { return; }
    const s = localStorage.getItem(SESSION_KEY);
    if (s) {
      api.post("/cart/merge", { session_id: s })
        .then(() => { setMerged(true); localStorage.removeItem(SESSION_KEY); return refresh(); })
        .catch(() => { setMerged(true); return refresh(); });
    } else {
      setMerged(true);
      refresh();
    }
  }, [user, merged, refresh]);

  useEffect(() => { refresh(); }, [refresh]);

  // Listen for external cart updates (e.g. AI agent tool executions)
  useEffect(() => {
    const handler = () => refresh();
    window.addEventListener("cart:updated", handler);
    return () => window.removeEventListener("cart:updated", handler);
  }, [refresh]);

  const addItem = async (programId, withIadaAval = false) => {
    try {
      const { data } = await api.post("/cart/items",
        { program_id: programId, with_iada_aval: !!withIadaAval },
        { headers: headers() });
      setCart(data);
      toast.success(withIadaAval ? "Agregado al carrito con aval IADA" : "Agregado al carrito");
      track("cart_item_added", { program_id: programId, with_iada_aval: !!withIadaAval });
      return true;
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (detail.startsWith("no_public_cohorts_available")) toast.error("Programa sin cohortes públicas disponibles.");
      else if (detail === "Ya estás inscrito en este programa.") toast.error(detail);
      else if (detail === "iada_aval_not_available_for_this_program") toast.error("Este programa no admite aval IADA.");
      else toast.error(formatApiError(detail));
      return false;
    }
  };

  // Fase 11 · Bundles
  const addBundle = async (bundleId, withIadaAval = false) => {
    const { data } = await api.post("/cart/items",
      { bundle_id: bundleId, with_iada_aval: !!withIadaAval },
      { headers: headers() });
    setCart(data);
    track("cart_bundle_added", { bundle_id: bundleId });
    return true;
  };

  const setItemIadaAval = async (programId, withIadaAval) => {
    try {
      const { data } = await api.put(`/cart/items/${programId}/iada-aval`,
        { with_iada_aval: !!withIadaAval }, { headers: headers() });
      setCart(data);
      track("cart_item_iada_toggled", { program_id: programId, with_iada_aval: !!withIadaAval });
      return true;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      return false;
    }
  };

  const removeItem = async (programId) => {
    try {
      const { data } = await api.delete(`/cart/items/${programId}`, { headers: headers() });
      setCart(data);
      track("cart_item_removed", { program_id: programId });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const applyDiscount = async (code) => {
    try {
      const { data } = await api.post("/cart/apply-discount", { code }, { headers: headers() });
      setCart(data);
      track("discount_applied", { code, discount_mxn: data.discount_mxn });
      toast.success(`Código ${code} aplicado`);
      return true;
    } catch (e) {
      toast.error("Código inválido o no aplicable");
      return false;
    }
  };

  const removeDiscount = async () => {
    try {
      const { data } = await api.delete("/cart/discount", { headers: headers() });
      setCart(data);
    } catch (e) { /* silent */ }
  };

  const clear = async () => {
    try {
      const { data } = await api.post("/cart/clear", null, { headers: headers() });
      setCart(data);
    } catch (e) { /* silent */ }
  };

  return (
    <Ctx.Provider value={{ cart, loading, refresh, addItem, addBundle, removeItem, setItemIadaAval, applyDiscount, removeDiscount, clear, headers }}>
      {children}
    </Ctx.Provider>
  );
}

export function useCart() {
  const c = useContext(Ctx);
  if (!c) return { cart: null, addItem: () => false, addBundle: () => false, removeItem: () => {}, setItemIadaAval: () => false, applyDiscount: () => false, removeDiscount: () => {}, clear: () => {}, refresh: () => {}, headers: () => ({}) };
  return c;
}

export function fmtMxn(n) {
  if (n === null || n === undefined) return "—";
  return "$" + Number(n).toLocaleString("es-MX") + " MXN";
}
